import { AdItemInterface, PlayerHookType, VideoPlayerInterface } from '@cbsinteractive/avia-js';
import { HookContextInterface } from '@cbsinteractive/avia-js/dist/types/iface/HookContextInterface';
import { IAdItem } from 'library/ts/player/adapter/interfaces/ads/IAdItem';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/hooks/AbstractAviaPlayerHook';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Ad hook to register with avia player when using DAI API
 */
export class AviaPlayerDaiApiAdHook extends AbstractAviaPlayerHook {

	/**
	 * @description Ad hook
	 */
	hookType: PlayerHookType = PlayerHookType.AD;

	adItem: IAdItem;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerDaiApiAdHook'
			}
		);

		this.tagEventsHandler( this.#onTagEvent, [
			TagEvent.ON_CREATE_PLAYER_SUCCESS
		] );
	}

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				// stop listening to this event... we need it once per instance to prevent memory leak
				this.removeTagEventsHandler(
					this.#onTagEvent,
					this.tag.eventDispatcher,
					[ TagEvent.ON_CREATE_PLAYER_SUCCESS ]
				).then(
					( data ) => {
						this.registerHook();
					}
				);
				break;
		}
	};

	/**
	 * @description Callback passed to player's registerHook function
	 * @param {HookContextInterface<AdItemInterface>} context
	 */
	callback = ( context: HookContextInterface<AdItemInterface> ): void => {
		try {
			const adItem = context?.value as AdItemInterface;
			const promoPrefixForPremium = 'paramount + - pre-roll - premium plan -';
			if ( adItem?.adTitle?.toLowerCase().startsWith( promoPrefixForPremium ) ) {
				// if premium, ad is skippable
				adItem.skippable = true;
				this?.logger?.log( 'DAI API ad hook callback', 'set adItem to skippable:', adItem );
			} else {
				this?.logger?.log( 'DAI API ad hook callback', 'adItem is not skippable:', adItem );
			}

			// inform application
			this.dispatchTagEvent(
				this.getTagEventVO(
					TagEvent.ON_PLAYER_HOOK_EXECUTED,
					this.tag,
					{
						player: this?.tag?.controller?.playerManager?.player,
						adaptedPlayerInstance: this?.tag?.controller?.playerManager?.playerAdapter?.adaptedPlayerInstance as VideoPlayerInterface,
						adItem: {
							skippable: adItem?.skippable
						},
						hook: this
					},
					this,
					this.name
				)
			);
		} catch ( error ) {
			this?.logger?.warn( 'DAI API ad hook callback error:', error );
		}
	};
}